/**
 * KeySetupScreen — the hard gate users land on after login when they have
 * no Anthropic key on file (V1-10).
 *
 * Single-purpose screen: collect a key, validate it server-side, save it.
 * No navigation elsewhere except Logout. Once saved successfully, the
 * AnthropicKeyContext refreshes and the gate flips, dropping the user into
 * the actual app at /jobs.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Typography, TextField, Button, Alert,
  IconButton, InputAdornment, CircularProgress, Stack, Divider, Link,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { useAuth } from '../context/AuthContext';
import { useAnthropicKey } from '../context/AnthropicKeyContext';
import { saveKey as apiSaveKey } from '../services/api';

export default function KeySetup() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { refresh } = useAnthropicKey();

  const [inputKey, setInputKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = inputKey.trim();
    // Client-side sanity checks first — saves a round-trip on obvious typos.
    if (!trimmed) {
      setError(t('settings.key_required'));
      return;
    }
    if (!trimmed.startsWith('sk-ant-')) {
      setError(t('settings.key_invalid'));
      return;
    }
    if (trimmed.length < 40) {
      setError(t('settings.key_too_short'));
      return;
    }
    setError('');
    setSaving(true);
    try {
      const res = await apiSaveKey(trimmed);
      if (!res.data?.ok) {
        setError(res.data?.error || t('settings.validation_failed'));
        setSaving(false);
        return;
      }
      // Success — flip the gate by refreshing the context. The
      // RequireAnthropicKey wrapper will see has_key=true and render
      // the real app on next render.
      await refresh();
      // No explicit navigation — the gate component re-renders the routed
      // children automatically.
    } catch (e2) {
      setError(e2.friendlyMessage || t('key_setup.save_failed'));
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'flex-start',
        py: { xs: 4, sm: 8 },
      }}
    >
      <Container maxWidth="sm">
        {/* Logout link top-right — always accessible so users never feel trapped. */}
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button
            size="small"
            color="inherit"
            startIcon={<LogoutIcon fontSize="small" />}
            onClick={signOut}
            sx={{ color: 'text.secondary' }}
          >
            {t('key_setup.logout')}
          </Button>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('key_setup.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            {t('key_setup.subtitle')}
          </Typography>

          {/* WHY block — keeps it short, plain language, no scary cost framing. */}
          <Box mb={3}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
              sx={{ letterSpacing: 0.5 }}
            >
              {t('key_setup.why_label')}
            </Typography>
            <Typography variant="body2" color="text.primary" mt={0.5}>
              {t('key_setup.why_body')}
            </Typography>
          </Box>

          {/* COST block — "real cost" panel, framed as accessible & generous,
              not as a barrier. Light blue tint to invite attention. */}
          <Box
            mb={3}
            p={2}
            sx={{
              bgcolor: 'info.50',
              border: '1px solid',
              borderColor: 'info.light',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="overline"
              color="info.dark"
              fontWeight={600}
              sx={{ letterSpacing: 0.5 }}
            >
              📊 {t('key_setup.cost_label')}
            </Typography>
            <Typography variant="body2" color="text.primary" mt={0.5}>
              {t('key_setup.cost_body')}
            </Typography>
          </Box>

          {/* HOW TO block — step-by-step instructions */}
          <Box mb={3}>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={600}
              sx={{ letterSpacing: 0.5 }}
            >
              📝 {t('key_setup.howto_label')}
            </Typography>
            <Typography variant="body2" color="text.primary" mt={0.5}>
              {t('key_setup.howto_body')}{' '}
              <Link
                href="https://console.anthropic.com/settings/keys"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}
              >
                console.anthropic.com
                <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Link>
              {t('key_setup.howto_outro')}
            </Typography>
          </Box>

          {/* DISCLAIMER — the "API ≠ Claude.ai subscription" point. Critical
              clarification we identified in the dependency audit. */}
          <Alert
            icon={<InfoOutlinedIcon fontSize="small" />}
            severity="info"
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="body2">
              {t('key_setup.disclaimer')}
            </Typography>
          </Alert>

          <Divider sx={{ mb: 3 }} />

          {/* Input form */}
          <form onSubmit={handleSubmit}>
            <TextField
              label={t('key_setup.input_label')}
              type={showKey ? 'text' : 'password'}
              fullWidth
              value={inputKey}
              onChange={(e) => {
                setInputKey(e.target.value);
                if (error) setError('');
              }}
              placeholder="sk-ant-api03-..."
              autoComplete="off"
              autoFocus
              error={Boolean(error)}
              helperText={error || ' '}
              disabled={saving}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowKey(s => !s)}
                      edge="end"
                      size="small"
                      aria-label={showKey ? t('settings.hide_key_aria') : t('settings.show_key_aria')}
                    >
                      {showKey ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            <Stack direction="row" spacing={2}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={!inputKey.trim() || saving}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {saving ? t('key_setup.connecting') : t('key_setup.connect_button')}
              </Button>
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}
